import React, { useEffect, useState } from "react";
import * as echarts from "echarts";
import { DatePicker, Space, Card, Row, Col, Button, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/en";
import useApiCall from "../common/api-call";
import { getUserStatistics } from "./statistics-service";

dayjs.locale("en");

const UserStatisticsView = () => {
  const { apiCall } = useApiCall();
  const [filteredData, setFilteredData] = useState([]);

  // Initialize dates
  const today = dayjs();
  const thirtyDaysAgo = dayjs().subtract(30, "day");

  const [startDate, setStartDate] = useState(thirtyDaysAgo.format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(today.format("YYYY-MM-DD"));
  const [startDateValue, setStartDateValue] = useState(thirtyDaysAgo);
  const [endDateValue, setEndDateValue] = useState(today);

  const fetchStatisticsData = async (start = startDate, end = endDate) => {
    try {
      const response = await getUserStatistics(apiCall, start, end);

      // Handle new response structure with statistics array
      if (
        response &&
        response.statistics &&
        Array.isArray(response.statistics)
      ) {
        setFilteredData(response.statistics);
      } else if (response && Array.isArray(response)) {
        // Fallback for old response format
        setFilteredData(response);
      } else {
        setFilteredData([]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchStatisticsData();
  }, []);

  const handleFetchData = () => {
    fetchStatisticsData(startDate, endDate);
  };

  // Calculate number of days between start and end date
  const calculateDaysBetween = (start, end) => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const timeDiff = endDateObj.getTime() - startDateObj.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
  };

  const daysBetween = calculateDaysBetween(startDate, endDate);

  // Prepare data for both charts
  const dayData = Array.from({ length: daysBetween }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      day: date.toISOString().split("T")[0],
      userCount: 0,
      dailyActiveUserCount: 0,
    };
  });

  filteredData?.forEach(item => {
    const dateIndex = dayData.findIndex(day => day.day === item.date);
    if (dateIndex !== -1) {
      dayData[dateIndex].userCount += item.userCount || 0;
      dayData[dateIndex].dailyActiveUserCount += item.dailyActiveUserCount || 0;
    }
  });

  const maxUserCount = Math.max(...dayData.map(item => item.userCount), 0);
  const maxDailyActiveUsers = Math.max(
    ...dayData.map(item => item.dailyActiveUserCount),
    0,
  );
  const userCountYAxisMax =
    maxUserCount > 0 ? Math.ceil(maxUserCount * 1.1) : 40;
  const dailyActiveYAxisMax =
    maxDailyActiveUsers > 0 ? Math.ceil(maxDailyActiveUsers * 1.1) : 40;

  // Chart options for User Count
  const getUserCountChartOption = () => ({
    title: {
      text: "Total Users",
      left: "center",
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "axis",
      formatter: params => {
        const { name, value } = params[0];
        return `Date: ${name}<br />User Count: ${value}`;
      },
    },
    legend: {
      data: ["User Count"],
      top: "12%",
    },
    grid: {
      top: "30%",
      left: "15%",
      right: "10%",
      bottom: "15%",
    },
    xAxis: {
      type: "category",
      data: dayData.map(item => item.day),
      axisLabel: {
        interval: Math.max(1, Math.floor(daysBetween / 10)),
        rotate: 45,
        margin: 10,
        formatter: value => dayjs(value).format("MM-DD"),
        showMaxLabel: true,
      },
      axisTick: {
        alignWithLabel: true,
        interval: Math.max(1, Math.floor(daysBetween / 15)),
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: userCountYAxisMax,
      interval: Math.max(1, Math.ceil(userCountYAxisMax / 10)),
      axisLabel: {
        formatter: value => Math.round(value).toLocaleString(),
        padding: [5, 5, 5, 5],
      },
      axisLine: {
        show: true,
      },
      axisTick: {
        show: true,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: "#ccc",
        },
      },
    },
    series: [
      {
        name: "User Count",
        type: "line",
        data: dayData.map(item => item.userCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#4caf50",
        },
      },
    ],
  });

  // Chart options for Daily Active Users
  const getDailyActiveUsersChartOption = () => ({
    title: {
      text: "Daily Active Users",
      left: "center",
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "axis",
      formatter: params => {
        const { name, value } = params[0];
        return `Date: ${name}<br />Daily Active Users: ${value}`;
      },
    },
    legend: {
      data: ["Daily Active Users"],
      top: "12%",
    },
    grid: {
      top: "30%",
      left: "15%",
      right: "10%",
      bottom: "15%",
    },
    xAxis: {
      type: "category",
      data: dayData.map(item => item.day),
      axisLabel: {
        interval: Math.max(1, Math.floor(daysBetween / 10)),
        rotate: 45,
        margin: 10,
        formatter: value => dayjs(value).format("MM-DD"),
        showMaxLabel: true,
      },
      axisTick: {
        alignWithLabel: true,
        interval: Math.max(1, Math.floor(daysBetween / 15)),
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: dailyActiveYAxisMax,
      interval: Math.max(1, Math.ceil(dailyActiveYAxisMax / 10)),
      axisLabel: {
        formatter: value => Math.round(value).toLocaleString(),
        padding: [5, 5, 5, 5],
      },
      axisLine: {
        show: true,
      },
      axisTick: {
        show: true,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: "#ccc",
        },
      },
    },
    series: [
      {
        name: "Daily Active Users",
        type: "line",
        data: dayData.map(item => item.dailyActiveUserCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#2196f3",
        },
      },
    ],
  });

  useEffect(() => {
    // Initialize User Count Chart
    const userCountChartDom = document.getElementById("user-count-chart");
    const userCountChart = echarts.init(userCountChartDom);
    userCountChart.setOption(getUserCountChartOption());

    // Initialize Daily Active Users Chart
    const dailyActiveChartDom = document.getElementById(
      "daily-active-users-chart",
    );
    const dailyActiveChart = echarts.init(dailyActiveChartDom);
    dailyActiveChart.setOption(getDailyActiveUsersChartOption());

    const handleResize = () => {
      userCountChart.resize();
      dailyActiveChart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      userCountChart.dispose();
      dailyActiveChart.dispose();
      window.removeEventListener("resize", handleResize);
    };
  }, [filteredData, startDate, endDate]);

  return (
    <div style={{ flexGrow: 1, height: "100%", margin: 16, paddingBottom: 80 }}>
      {/* Date Selection Controls */}
      <Card
        style={{
          marginBottom: 24,
          border: "1px solid #e0e0e0",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography.Title level={5} style={{ marginBottom: 16, color: "#333" }}>
          Date Range Selection
        </Typography.Title>
        <Row gutter={16} align="bottom">
          <Col xs={24} sm={10}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                Start Date
              </Typography.Text>
              <DatePicker
                value={startDateValue}
                onChange={(date) => {
                  setStartDateValue(date);
                  setStartDate(date ? date.format("YYYY-MM-DD") : "");
                }}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
            />
            </Space>
          </Col>
          <Col xs={24} sm={10}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                End Date
              </Typography.Text>
              <DatePicker
                value={endDateValue}
                onChange={(date) => {
                  setEndDateValue(date);
                  setEndDate(date ? date.format("YYYY-MM-DD") : "");
                }}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
            />
            </Space>
          </Col>
          <Col xs={24} sm={4}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleFetchData}
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* User Count Chart */}
      <div style={{ width: "100%", height: "400px", marginBottom: 24 }} className="chart-container">
        <div id="user-count-chart" style={{ width: "100%", height: "100%" }} />
      </div>

      {/* Daily Active Users Chart */}
      <div style={{ width: "100%", height: "400px" }} className="chart-container">
        <div
          id="daily-active-users-chart"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    </div>
  );
};

export default UserStatisticsView;
